// route-map brick (presentation-2 contract section 5.3): a board of nodes and segmented edges on a
// material panel. Edge colors come from a theme map over the edge's `fields.color`; claimed edges
// draw in the owner's seat color with a light inner stripe; claimable edges glow. Geometry is
// presentation only: legality and connectivity stay with the host.

import { useEffect, useMemo, useState, useRef, type PointerEvent } from 'react';
import type { BrickInstance } from '../presentation/types';
import type { ObjectView } from '../model/table';
import type { BoardEdge, BoardNode } from '../protocol/ui';
import { useScene, zonesFor, seatOf } from '../scene/SceneContext';
import { targetProps, usePlay } from '../play/PlayController';
import { objectAnchor } from '../play/targets';
import { boardLayout } from '../components/BoardView';
import { useMotionFlags } from '../motion/MotionLayer';
import { artAsset } from '../art/assets';
import { SvgImage } from '../art/SvgImage';

interface BrickProps { inst: BrickInstance; seat: string | null }
interface View { x: number; y: number; w: number; h: number }

const FALLBACK_COLORS: Record<string, string> = {
  red: '#c8372d', orange: '#e07b27', yellow: '#e6bf34', green: '#3f8f3a', blue: '#2f6db5', pink: '#d46a9a', purple: '#d46a9a',
  white: '#f4efe3', black: '#2b2b2b', gray: '#b3ad9f', grey: '#b3ad9f',
};

export function RouteMap({ inst, seat }: BrickProps) {
  const { table, presentation, seatColor, board } = useScene();
  const play = usePlay();
  const flags = useMotionFlags();
  const zone = zonesFor(inst, table, seatOf(inst, seat, table.selfSeat))[0];
  const found = zone?.boardId ? board(zone.boardId) : null;
  const aspect = (inst.props?.aspect as number | undefined) ?? 1;
  const zoomable = inst.props?.zoom !== false;
  // Schematic coordinates may be stretched horizontally to fit the area; geometry is presentation only.
  const b = useMemo(() => (found ? (aspect === 1 ? found.board : {
    ...found.board, width: found.board.width * aspect, nodes: found.board.nodes.map(n => ({ ...n, x: n.x * aspect })),
  }) : null), [found, aspect]);
  const layout = useMemo(() => (b ? boardLayout(b) : null), [b]);
  const full: View = b ? { x: 0, y: 0, w: b.width, h: b.height } : { x: 0, y: 0, w: 1, h: 1 };
  const [view, setView] = useState<View>(full);
  const drag = useRef<{ x: number; y: number; view: View; moved: boolean } | null>(null);
  const svg = useRef<SVGSVGElement | null>(null);
  // Screen pixels per board unit at the current zoom; labels never draw below MIN_LABEL_PX.
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = svg.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => { if (e) setBox({ w: e.contentRect.width, h: e.contentRect.height }); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [b]);
  const scale = box && box.w > 0 && box.h > 0 ? Math.min(box.w / view.w, box.h / view.h) : null;
  const baseFont = layout ? layout.unit * 1.75 : 1;
  // Quantized so small pans or resizes do not re-run placement.
  const font = scale && baseFont * scale < MIN_LABEL_PX ? Math.ceil((MIN_LABEL_PX / scale) * 4) / 4 : baseFont;
  const crowded = font > baseFont;
  // When crowded, labels stay inside the board and clear of the zoom toolbar (top right, about 110 by 44 px).
  const toolbar = crowded && scale && zoomable && view.w >= full.w ? [[view.x + view.w - 110 / scale, view.y, view.x + view.w, view.y + 44 / scale] as Box] : [];
  const labels = useMemo(() => (b && layout ? placeLabels(b.nodes, layout.unit, font, crowded, crowded ? [0, 0, b.width, b.height] : undefined, toolbar) : new Map<string, LabelPlace>()),
    [b, layout, font, crowded, toolbar.length]);
  if (!zone || !b || !layout) return null;

  const colorMap = inst.props?.colorMap ? presentation.theme.maps?.[inst.props.colorMap as string] : undefined;
  const edgeColor = (e: BoardEdge) => {
    const c = String(e.fields?.color ?? 'gray');
    return colorMap?.[c] ?? FALLBACK_COLORS[c] ?? '#b3ad9f';
  };
  const rails = inst.props?.edgeStyle !== 'segments';
  const gap = (inst.props?.parallelGap as number | undefined) ?? 1;
  const byRef = new Map<string, ObjectView>();
  for (const o of zone.objects) if (o.ref) byRef.set(o.ref, o);
  const unit = layout.unit;
  const background = typeof inst.props?.background === 'string' ? artAsset(presentation, inst.props.background.replace(/^asset:/, ''), box?.w ?? 800) : null;
  const highlight = play?.highlight ?? null;

  const zoomBy = (factor: number) => setView(v => {
    const w = Math.min(full.w, Math.max(full.w / 6, v.w * factor)), h = w * (full.h / full.w);
    return clamp({ x: v.x + v.w / 2 - w / 2, y: v.y + v.h / 2 - h / 2, w, h }, full);
  });
  const down = (e: PointerEvent<SVGSVGElement>) => { if (view.w < full.w) drag.current = { x: e.clientX, y: e.clientY, view, moved: false }; };
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d || !svg.current) return;
    const r = svg.current.getBoundingClientRect();
    if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 4) d.moved = true;
    if (d.moved) setView(clamp({ ...d.view, x: d.view.x - (e.clientX - d.x) * (d.view.w / r.width), y: d.view.y - (e.clientY - d.y) * (d.view.h / r.height) }, full));
  };
  const up = () => { setTimeout(() => { drag.current = null; }, 0); };

  return (
    <section className="route-map" aria-label={b.label} data-motion-zone={zone.id}>
      {zoomable && (
        <div className="map-tools" role="toolbar" aria-label={`${b.label} zoom`}>
          <button type="button" aria-label="Zoom in" onClick={() => zoomBy(0.7)}>+</button>
          <button type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / 0.7)} disabled={view.w >= full.w}>−</button>
          <button type="button" aria-label="Show the whole board" onClick={() => setView(full)} disabled={view.w >= full.w}>⤢</button>
        </div>
      )}
      <svg ref={svg} className={`map-svg${highlight ? ' has-highlight' : ''}`} viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} role="group" aria-label={b.label}
        preserveAspectRatio="xMidYMid meet" style={{ '--board-unit': `${unit}px` } as React.CSSProperties} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}>
        {background && <g aria-hidden="true" pointerEvents="none"><SvgImage href={background.url} x={0} y={0} width={b.width} height={b.height} preserveAspectRatio="none" /></g>}
        <g className="map-edges">
          {b.edges.map(e => {
            const a = layout.nodes.get(e.a), z = layout.nodes.get(e.b);
            if (!a || !z) return null;
            const o = byRef.get(e.id);
            const anchor = o ? objectAnchor(o.id) : `edge:${e.id}`;
            const t = targetProps(play, anchor, o?.label ?? e.label, `map-edge${o?.owner ? ' is-claimed' : ''}`);
            const claiming = o ? flags.claiming.get(o.id) : undefined;
            const color = o?.owner ? seatColor(o.owner) : edgeColor(e);
            return (
              <g key={e.id} {...t} {...(o ? { 'data-motion-id': o.id } : {})} onClick={ev => { if (drag.current?.moved) return; t.onClick(ev); }}
                style={{ '--edge-color': color, '--claim-ms': claiming ? `${claiming}ms` : undefined } as React.CSSProperties}>
                <title>{o?.label ?? e.label}</title>
                {segments(e, a, z, unit, (layout.offsets.get(e.id) ?? 0) * gap, rails, !!o?.owner, !!claiming)}
              </g>
            );
          })}
        </g>
        <g className="map-nodes">
          {b.nodes.map(n => <City key={n.id} node={n} unit={unit} font={font} label={labels.get(n.id)} lit={highlight?.has(n.id) ?? false} dim={!!highlight && !highlight.has(n.id)} object={byRef.get(n.id)} />)}
        </g>
      </svg>
    </section>
  );
}

function City({ node, unit, font, label, lit, dim, object }: { node: BoardNode; unit: number; font: number; label?: LabelPlace; lit: boolean; dim: boolean; object?: ObjectView }) {
  const play = usePlay();
  const anchor = object ? objectAnchor(object.id) : `node:${node.id}`;
  const t = object ? targetProps(play, anchor, object.label, 'map-city') : null;
  const r = unit * 1.25;
  return (
    <g {...(t ?? { 'aria-hidden': true })} className={`${t?.className ?? 'map-city'}${lit ? ' is-lit' : ''}${dim ? ' is-faded' : ''}`}>
      {lit && <circle cx={node.x} cy={node.y} r={r * 2.1} className="city-halo" />}
      <circle cx={node.x} cy={node.y} r={r} className="city-ring" />
      <circle cx={node.x} cy={node.y} r={r * 0.38} className="city-dot" />
      <title>{node.label}</title>
      {label?.text !== null && (
        <text x={node.x + (label?.dx ?? 0)} y={node.y + (label?.dy ?? -r * 1.55)} className="city-label" textAnchor={label?.anchor ?? 'middle'} fontSize={font}>{label?.text ?? node.label}</text>
      )}
    </g>
  );
}

/** Smallest city label in screen pixels; below it labels grow, abbreviate or yield to neighbours. */
export const MIN_LABEL_PX = 11;

/** `text` is the drawn label (full or abbreviated), or null when it yields to a neighbour. */
export interface LabelPlace { dx: number; dy: number; anchor: 'start' | 'middle' | 'end'; text: string | null }

type Box = [number, number, number, number];

/** A short form for crowded maps: initials for several words (Salt Lake City → SLC), else the first four letters. */
export function abbreviate(label: string): string {
  const words = label.split(/[\s-]+/).map(w => w.replace(/[^\p{L}]/gu, '')).filter(Boolean);
  if (words.length > 1) return words.map(w => w[0]!.toUpperCase()).join('');
  const word = words[0] ?? label;
  return word.length > 5 ? `${word.slice(0, 4)}.` : word;
}

/**
 * Greedy label placement around each city, avoiding cities and earlier labels. At the natural size
 * a label that fits nowhere keeps the first position. When `crowded` (labels enlarged to stay
 * legible on a small screen) it tries more positions, then a unique abbreviation, and otherwise
 * yields: the city keeps its name as a tooltip and the label returns when the player zooms in.
 */
export function placeLabels(nodes: BoardNode[], unit: number, fs: number = unit * 1.75, crowded = false, bounds?: Box, obstacles: Box[] = []): Map<string, LabelPlace> {
  const r = unit * 1.25;
  const pad = crowded ? fs * 0.12 : 0;
  const boxes: Box[] = [...nodes.map((n): Box => [n.x - r, n.y - r, n.x + r, n.y + r]), ...obstacles];
  const out = new Map<string, LabelPlace>();
  const outside = (a: Box) => !!bounds && (a[0] < bounds[0] || a[1] < bounds[1] || a[2] > bounds[2] || a[3] > bounds[3]);
  const hits = (a: Box) => outside(a) || boxes.some(b => a[0] - pad < b[2] && a[2] + pad > b[0] && a[1] - pad < b[3] && a[3] + pad > b[1]);
  const short = new Map(nodes.map(n => [n.id, abbreviate(n.label)]));
  const uses = new Map<string, number>();
  for (const v of short.values()) uses.set(v, (uses.get(v) ?? 0) + 1);
  const off = r * 1.55;
  const candidates = (n: BoardNode, text: string): [LabelPlace, Box][] => {
    const w = text.length * fs * (crowded ? 0.62 : 0.56);
    const above: Box = [n.x - w / 2, n.y - off - fs * 0.85, n.x + w / 2, n.y - off + fs * 0.2];
    const below: Box = [n.x - w / 2, n.y + off - fs * 0.1, n.x + w / 2, n.y + off + fs * 0.9];
    const list: [LabelPlace, Box][] = [
      [{ dx: 0, dy: -off, anchor: 'middle', text }, above],
      [{ dx: 0, dy: off + fs * 0.75, anchor: 'middle', text }, below],
      [{ dx: r * 1.5, dy: fs * 0.32, anchor: 'start', text }, [n.x + r * 1.5, n.y - fs * 0.55, n.x + r * 1.5 + w, n.y + fs * 0.45]],
      [{ dx: -r * 1.5, dy: fs * 0.32, anchor: 'end', text }, [n.x - r * 1.5 - w, n.y - fs * 0.55, n.x - r * 1.5, n.y + fs * 0.45]],
    ];
    if (crowded) list.push(
      [{ dx: r, dy: -off, anchor: 'start', text }, [n.x + r, above[1], n.x + r + w, above[3]]],
      [{ dx: -r, dy: -off, anchor: 'end', text }, [n.x - r - w, above[1], n.x - r, above[3]]],
      [{ dx: r, dy: off + fs * 0.75, anchor: 'start', text }, [n.x + r, below[1], n.x + r + w, below[3]]],
      [{ dx: -r, dy: off + fs * 0.75, anchor: 'end', text }, [n.x - r - w, below[1], n.x - r, below[3]]],
    );
    return list;
  };
  const yielded: BoardNode[] = [];
  for (const n of [...nodes].sort((a, b) => a.y - b.y || a.x - b.x)) {
    const list = candidates(n, n.label);
    const pick = list.find(([, box]) => !hits(box)) ?? (crowded ? undefined : list[0]);
    if (pick) { out.set(n.id, pick[0]); boxes.push(pick[1]); } else yielded.push(n);
  }
  for (const n of yielded) {
    const s = short.get(n.id)!;
    const pick = s !== n.label && uses.get(s) === 1 ? candidates(n, s).find(([, box]) => !hits(box)) : undefined;
    if (pick) { out.set(n.id, pick[0]); boxes.push(pick[1]); } else out.set(n.id, { dx: 0, dy: -off, anchor: 'middle', text: null });
  }
  return out;
}

function clamp(v: View, full: View): View {
  return { ...v, x: Math.min(Math.max(v.x, 0), full.w - v.w), y: Math.min(Math.max(v.y, 0), full.h - v.h) };
}

function segments(e: BoardEdge, a: BoardNode, b: BoardNode, unit: number, offset: number, rails: boolean, claimed: boolean, claiming: boolean) {
  const count = e.segments ?? 1;
  const dx = b.x - a.x, dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  const inset = unit * 1.5;
  const usable = Math.max(length - inset * 2, 1);
  const gap = Math.min(unit * 0.32, usable / (count * 4));
  const seg = (usable - gap * (count - 1)) / count;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const h = unit * 1.15;
  const items = [<line key="glow" x1={inset} y1={offset} x2={inset + usable} y2={offset} className="edge-glow" strokeWidth={h * 2.4} />];
  for (let i = 0; i < count; i++) {
    const x = inset + i * (seg + gap);
    items.push(
      <g key={i} className="edge-seg" style={claiming ? ({ '--i': i } as React.CSSProperties) : undefined}>
        <rect x={x} y={offset - h / 2} width={seg} height={h} rx={h * 0.28} className="seg-fill" />
        {claimed && <rect x={x + seg * 0.18} y={offset - h * 0.12} width={seg * 0.64} height={h * 0.24} rx={h * 0.12} className="seg-stripe" />}
        {rails && !claimed && <line x1={x + seg * 0.5} y1={offset - h * 0.32} x2={x + seg * 0.5} y2={offset + h * 0.32} className="seg-tie" />}
      </g>,
    );
  }
  items.push(<line key="hit" x1={inset} y1={offset} x2={inset + usable} y2={offset} className="edge-hit" strokeWidth={h * 1.8} />);
  return <g transform={`translate(${a.x} ${a.y}) rotate(${angle})`}>{items}</g>;
}
